// client/src/coms/views/canvas_parts/canvas_room_view_parts/selectionUtils.js
function normalizeSelectionVertex(vertex) {
  if (!vertex || typeof vertex !== 'object') return null;
  const x = Number(vertex.x);
  const y = Number(vertex.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y };
}

export function normalizeSelectionVertices(vertices) {
  if (!Array.isArray(vertices)) return [];
  return vertices.map(normalizeSelectionVertex).filter(Boolean);
}

export function boundsFromVertices(vertices) {
  const safeVertices = normalizeSelectionVertices(vertices);
  if (!safeVertices.length) return null;

  let left = safeVertices[0].x;
  let right = safeVertices[0].x;
  let top = safeVertices[0].y;
  let bottom = safeVertices[0].y;

  safeVertices.forEach((vertex) => {
    left = Math.min(left, vertex.x);
    right = Math.max(right, vertex.x);
    top = Math.min(top, vertex.y);
    bottom = Math.max(bottom, vertex.y);
  });

  return {
    x: Math.floor(left),
    y: Math.floor(top),
    width: Math.max(1, Math.ceil(right - left)),
    height: Math.max(1, Math.ceil(bottom - top)),
  };
}

function pointInPolygon(point, vertices) {
  const safeVertices = normalizeSelectionVertices(vertices);
  if (!point || safeVertices.length < 3) return false;

  let inside = false;
  for (let i = 0, j = safeVertices.length - 1; i < safeVertices.length; j = i, i += 1) {
    const xi = safeVertices[i].x;
    const yi = safeVertices[i].y;
    const xj = safeVertices[j].x;
    const yj = safeVertices[j].y;
    const intersects = ((yi > point.y) !== (yj > point.y))
      && point.x < (((xj - xi) * (point.y - yi)) / ((yj - yi) || 0.000001)) + xi;
    if (intersects) inside = !inside;
  }

  return inside;
}

function createBoundsVertices(bounds) {
  if (!bounds) return [];
  const x = Number(bounds.x);
  const y = Number(bounds.y);
  const width = Number(bounds.width);
  const height = Number(bounds.height);
  if (![x, y, width, height].every(Number.isFinite)) return [];
  return [
    { x, y },
    { x: x + width, y },
    { x: x + width, y: y + height },
    { x, y: y + height },
  ];
}

function pointInSelectionShape(point, selection) {
  if (!point || !selection || !selection.bounds) return false;

  if (selection.shape === 'combined_clip') {
    const areaSelection = selection.canvasAreaSelection || null;
    const baseSelection = selection.baseSelection || null;
    return (!areaSelection || pointInSelection(point, areaSelection))
      && (!baseSelection || pointInSelection(point, baseSelection));
  }

  if (selection.shape === 'canvas_area_slots') {
    const slots = Array.isArray(selection.slots) ? selection.slots : [];
    return slots.some((slot) => pointInSelection(point, slot));
  }

  const { x, y, width, height } = selection.bounds;
  if (point.x < x || point.x > x + width || point.y < y || point.y > y + height) {
    return false;
  }

  if (selection.shape === 'polygon') {
    return pointInPolygon(point, selection.vertices);
  }

  if (selection.shape === 'ellipse') {
    const rx = width / 2;
    const ry = height / 2;
    const cx = x + rx;
    const cy = y + ry;
    const dx = point.x - cx;
    const dy = point.y - cy;
    if (rx <= 0 || ry <= 0) return false;
    return ((dx * dx) / (rx * rx)) + ((dy * dy) / (ry * ry)) <= 1;
  }

  return true;
}

export function pointInSelection(point, selection) {
  if (!point || !selection || !selection.bounds) return false;
  const insideShape = pointInSelectionShape(point, selection);
  return selection.inverted ? !insideShape : insideShape;
}

export function selectionShapeContainsPoint(selection, x, y) {
  if (!selection || !selection.bounds) return false;
  return pointInSelectionShape({ x, y }, selection);
}

export function selectionContainsPoint(selection, x, y) {
  if (!selection) return true;
  if (selection.pixelMask instanceof Uint8Array) {
    const width = Math.max(1, Math.floor(Number(selection.maskWidth) || 0));
    const height = Math.max(1, Math.floor(Number(selection.maskHeight) || 0));
    const pixelX = Math.floor(Number(x));
    const pixelY = Math.floor(Number(y));
    if (pixelX < 0 || pixelY < 0 || pixelX >= width || pixelY >= height) return false;
    const inside = selection.pixelMask[(pixelY * width) + pixelX] > 0;
    return selection.inverted ? !inside : inside;
  }
  if (!selection.bounds) return true;
  return pointInSelection({ x, y }, selection);
}

export function normalizeBounds(start, end) {
  const left = Math.min(start.x, end.x);
  const top = Math.min(start.y, end.y);
  const right = Math.max(start.x, end.x);
  const bottom = Math.max(start.y, end.y);
  return {
    x: Math.floor(left),
    y: Math.floor(top),
    width: Math.max(1, Math.floor(right - left)),
    height: Math.max(1, Math.floor(bottom - top)),
  };
}

function appendSelectionSubPath(ctx, shape, bounds, vertices = []) {
  if (!ctx || !bounds) return;

  if (shape === 'polygon') {
    const safeVertices = normalizeSelectionVertices(vertices);
    if (safeVertices.length >= 3) {
      ctx.moveTo(safeVertices[0].x, safeVertices[0].y);
      safeVertices.slice(1).forEach((vertex) => ctx.lineTo(vertex.x, vertex.y));
      ctx.closePath();
      return;
    }
  }

  if (shape === 'ellipse') {
    ctx.ellipse(
      bounds.x + bounds.width / 2,
      bounds.y + bounds.height / 2,
      Math.max(0.5, bounds.width / 2),
      Math.max(0.5, bounds.height / 2),
      0,
      0,
      Math.PI * 2,
    );
    return;
  }

  ctx.rect(bounds.x, bounds.y, bounds.width, bounds.height);
}


function appendSelectionObjectPath(ctx, selection) {
  if (!ctx || !selection || !selection.bounds) return false;

  if (selection.shape === 'canvas_area_slots') {
    const slots = Array.isArray(selection.slots) ? selection.slots : [];
    let appended = false;
    slots.forEach((slot) => {
      if (!slot || !slot.bounds) return;
      appendSelectionSubPath(ctx, slot.shape || 'polygon', slot.bounds, slot.vertices);
      appended = true;
    });
    return appended;
  }

  appendSelectionSubPath(ctx, selection.shape, selection.bounds, selection.vertices);
  return true;
}

export function drawSelectionPath(ctx, shape, bounds, vertices = []) {
  if (!ctx || !bounds) return;
  ctx.beginPath();
  appendSelectionSubPath(ctx, shape, bounds, vertices);
}

export function drawSelectionOutline(ctx, selection, dashed = true) {
  if (!ctx || !selection || !selection.bounds) return;

  ctx.save();
  if (dashed) {
    ctx.setLineDash([6, 4]);
    ctx.lineDashOffset = 0;
  }
  ctx.strokeStyle = 'rgba(255,255,255,0.95)';
  ctx.lineWidth = 1;
  drawSelectionPath(ctx, selection.shape, selection.bounds, selection.vertices);
  ctx.stroke();
  ctx.setLineDash([6, 4]);
  ctx.lineDashOffset = 5;
  ctx.strokeStyle = 'rgba(0,0,0,0.85)';
  drawSelectionPath(ctx, selection.shape, selection.bounds, selection.vertices);
  ctx.stroke();

  if (selection.shape === 'polygon') {
    const safeVertices = normalizeSelectionVertices(selection.vertices);
    ctx.setLineDash([]);
    ctx.fillStyle = 'rgba(255,255,255,0.95)';
    ctx.strokeStyle = 'rgba(0,0,0,0.85)';
    safeVertices.forEach((vertex) => {
      ctx.beginPath();
      ctx.arc(vertex.x, vertex.y, 3.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    });
  }

  ctx.restore();
}

export function createSelectionTempCanvas(imageData) {
  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = imageData.width;
  tempCanvas.height = imageData.height;
  const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true });
  if (!tempCtx) return null;
  tempCtx.putImageData(imageData, 0, 0);
  return tempCanvas;
}

function clipTempContextToSelection(tempCtx, selection, width, height) {
  if (!tempCtx || !selection || !selection.bounds) return;

  if (selection.shape === 'ellipse') {
    tempCtx.beginPath();
    tempCtx.ellipse(width / 2, height / 2, Math.max(0.5, width / 2), Math.max(0.5, height / 2), 0, 0, Math.PI * 2);
    tempCtx.clip();
    return;
  }

  if (selection.shape === 'polygon') {
    const safeVertices = normalizeSelectionVertices(selection.vertices);
    if (safeVertices.length < 3) return;
    tempCtx.beginPath();
    tempCtx.moveTo(safeVertices[0].x - selection.bounds.x, safeVertices[0].y - selection.bounds.y);
    safeVertices.slice(1).forEach((vertex) => tempCtx.lineTo(vertex.x - selection.bounds.x, vertex.y - selection.bounds.y));
    tempCtx.closePath();
    tempCtx.clip();
  }
}

export function getSelectionImageData(layerCanvas, selection) {
  if (!layerCanvas || !selection || !selection.bounds) return null;
  const { x, y, width, height } = selection.bounds;
  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = width;
  tempCanvas.height = height;
  const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true });
  if (!tempCtx) return null;

  tempCtx.save();
  clipTempContextToSelection(tempCtx, selection, width, height);
  tempCtx.drawImage(layerCanvas, x, y, width, height, 0, 0, width, height);
  tempCtx.restore();

  try {
    return tempCtx.getImageData(0, 0, width, height);
  } catch {
    return null;
  }
}

export function clearSelectionFromLayer(layerCanvas, selection) {
  if (!layerCanvas || !selection || !selection.bounds) return;
  const ctx = layerCanvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return;

  const { x, y, width, height } = selection.bounds;
  ctx.save();
  ctx.globalCompositeOperation = 'destination-out';
  ctx.fillStyle = 'rgba(0,0,0,1)';
  if (selection.shape === 'polygon') {
    drawSelectionPath(ctx, selection.shape, selection.bounds, selection.vertices);
    ctx.fill();
  } else if (selection.shape === 'ellipse') {
    ctx.beginPath();
    ctx.ellipse(x + width / 2, y + height / 2, Math.max(0.5, width / 2), Math.max(0.5, height / 2), 0, 0, Math.PI * 2);
    ctx.fill();
  } else {
    ctx.fillRect(x, y, width, height);
  }
  ctx.restore();
}

export function drawFloatingSelection(previewCtx, selection) {
  if (!previewCtx || !selection || !selection.floatingImageData || !selection.bounds) return;
  const tempCanvas = createSelectionTempCanvas(selection.floatingImageData);
  if (!tempCanvas) return;
  previewCtx.drawImage(tempCanvas, selection.bounds.x, selection.bounds.y);
}

export function clipContextToSelection(ctx, selection) {
  if (!ctx || !selection || !selection.bounds) return false;
  ctx.save();

  if (selection.shape === 'combined_clip') {
    const areaSelection = selection.canvasAreaSelection || null;
    const baseSelection = selection.baseSelection || null;
    let clipped = false;

    if (areaSelection && areaSelection.bounds) {
      ctx.beginPath();
      if (appendSelectionObjectPath(ctx, areaSelection)) {
        ctx.clip();
        clipped = true;
      }
    }

    if (baseSelection && baseSelection.bounds) {
      ctx.beginPath();
      if (baseSelection.inverted) {
        ctx.rect(0, 0, Math.max(1, ctx.canvas ? ctx.canvas.width : 1), Math.max(1, ctx.canvas ? ctx.canvas.height : 1));
        appendSelectionObjectPath(ctx, baseSelection);
        try {
          ctx.clip('evenodd');
        } catch {
          ctx.clip();
        }
      } else if (appendSelectionObjectPath(ctx, baseSelection)) {
        ctx.clip();
      }
      clipped = true;
    }

    if (!clipped) {
      ctx.restore();
      return false;
    }
    return true;
  }

  if (selection.inverted) {
    ctx.beginPath();
    ctx.rect(0, 0, Math.max(1, ctx.canvas ? ctx.canvas.width : 1), Math.max(1, ctx.canvas ? ctx.canvas.height : 1));
    appendSelectionObjectPath(ctx, selection);
    try {
      ctx.clip('evenodd');
    } catch {
      ctx.clip();
    }
    return true;
  }

  ctx.beginPath();
  if (!appendSelectionObjectPath(ctx, selection)) {
    ctx.restore();
    return false;
  }
  ctx.clip();
  return true;
}

